<script setup>
// The forecast inside Trail conditions: the Forecast block's strip
// (forecastSlots.js), drawn one of three ways —
//   inside       a band within the conditions panel, ruled off from it
//   card         a white panel of its own, when there is no conditions
//                panel to join
//   transparent  straight on the block's background
import { forecastEmoji, forecastTemp } from '../forecastSlots.js';

defineProps({
    slots: { type: Array, required: true },
    mode: { type: String, default: 'hourly' },
    heading: { type: String, default: '' },
    subtitle: { type: String, default: '' },
    unitSystem: { type: String, default: 'metric' },
    variant: { type: String, default: 'transparent' },
    position: { type: String, default: 'bottom' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
    accent: { type: String, default: '' },
});
</script>

<template>
    <div class="th-conds-fc" :class="[`th-conds-fc--${variant}`, `th-conds-fc--${position}`]">
        <div v-if="heading || subtitle" class="th-conds-fc__head">
            <span v-if="heading" class="th-conds-fc__heading">{{ heading }}</span>
            <span v-if="subtitle" class="th-conds-fc__sub">{{ subtitle }}</span>
        </div>
        <div class="th-conds-fc__strip">
            <div v-for="(s, i) in slots" :key="i" class="th-conds-fc__slot">
                <div class="th-conds-fc__when">{{ s.when }}</div>
                <div class="th-conds-fc__temp">
                    <span class="th-conds-fc__icon" aria-hidden="true">{{ forecastEmoji(s.code) }}</span>
                    <template v-if="mode === 'daily'">
                        {{ forecastTemp(s.high_c, unitSystem) }}<span class="th-conds-fc__low">{{ forecastTemp(s.low_c, unitSystem) }}</span>
                    </template>
                    <template v-else>{{ forecastTemp(s.temp_c, unitSystem) }}</template>
                </div>
                <div v-if="s.summary" class="th-conds-fc__sky">{{ s.summary }}</div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.th-conds-fc__head { display: flex; flex-wrap: wrap; align-items: baseline; gap: 4px 12px; margin-bottom: 10px; }
.th-conds-fc__heading { font: 700 18px/1.2 var(--th-display); color: var(--text-heading, inherit); }
.th-conds-fc__sub { font-size: 14px; color: var(--text-sub, var(--th-muted)); }
/* The design's one joined strip in every variant: readings divided by
   rules, never boxed. White puts it on the conditions panel (or a panel
   of its own); transparent leaves it on the block's background. */
.th-conds-fc__strip { display: flex; flex-wrap: wrap; overflow: hidden; }
.th-conds-fc__slot {
    padding: 18px 20px;
    /* Every reading as wide as the row allows: a short last row (five
       days on a phone that fits three) STRETCHES rather than leaving a
       hole, and nothing is narrower than a reading needs, so a long
       high/low cannot run past the panel's edge. */
    flex: 1 1 128px;
    /* Rules drawn as shadows on the right and below; the strip clips
       the ones that land on its outer edge, wherever the rows break. */
    box-shadow: 1px 0 0 var(--th-line-soft), 0 1px 0 var(--th-line-soft);
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
}
.th-conds-fc__when { font: 500 11px/1 var(--th-mono); letter-spacing: .1em; text-transform: uppercase; color: var(--th-meta); }
.th-conds-fc__temp { font: 700 22px/1.1 var(--th-display); display: flex; flex-wrap: wrap; align-items: baseline; gap: 2px 6px; }
.th-conds-fc__icon { font-size: 18px; line-height: 1; }
.th-conds-fc__low { color: var(--th-faint); font-weight: 500; font-size: 16px; }
.th-conds-fc__sky { font-size: 14px; color: var(--text-sub, var(--th-muted)); }

.th-conds-fc--inside, .th-conds-fc--card { background: #fff; }
.th-conds-fc--inside .th-conds-fc__head, .th-conds-fc--card .th-conds-fc__head { padding: 16px 20px 0; margin-bottom: 0; }
.th-conds-fc--inside.th-conds-fc--top { border-bottom: 1px solid var(--th-line); }
.th-conds-fc--inside.th-conds-fc--bottom { border-top: 1px solid var(--th-line); }
.th-conds-fc--card { border: 1px solid var(--th-line); border-radius: var(--r); overflow: hidden; }

/* Transparent: no box at all, the first reading flush with the
   conditions panel's edge. */
.th-conds-fc--transparent .th-conds-fc__slot { padding: 14px 18px; }
.th-conds-fc--transparent .th-conds-fc__slot:first-child { padding-left: 0; }

.th-conds-fc--card.th-conds-fc--top, .th-conds-fc--transparent.th-conds-fc--top { margin-bottom: 16px; }
.th-conds-fc--card.th-conds-fc--bottom, .th-conds-fc--transparent.th-conds-fc--bottom { margin-top: 16px; }
@media (max-width: 480px) {
    .th-conds-fc__slot { padding: 14px 16px; }
}
</style>
